import type { RabEntry } from '../../types';
import type { FrageStatus, Planungsfrage } from '../planungsphase/types';
import { sortiereFragen } from '../planungsphase/planungLogic';
import { pflichtAus, type ProjektSteckbrief } from '../../services/projektSteckbrief';

/**
 * Die Unterlage fuer spaetere Arbeiten liest die Planungsphase
 * (docs/planungsphase-automatisierung-plan.md, Stufe 3.4).
 *
 * Alles hier ist pur: Fragen hinein, Vorschlaege heraus. Geschrieben wird
 * nirgends — die Uebernahme in den Massnahmenkatalog ist eine VORBELEGUNG des
 * Eintragsformulars, gespeichert wird erst, wenn der Nutzer dort auf
 * „Eintrag hinzufuegen" klickt. Den Stempel `uebernommen_am` setzt die
 * Ansicht per Klick ueber `markiereUebernommen` (Entscheid E7).
 */

// ═══════════════════════════════════════════════════════════════════════════
// FRAGEN MIT ZIEL „UNTERLAGE" → VORSCHLAGSKARTEN
// ═══════════════════════════════════════════════════════════════════════════

export interface UfsaFrageVorschlag {
    /** `planungsfragen.id` — den braucht der Stempel; der Steckbrief fuehrt nur Codes. */
    id: string;
    code: string;
    frage: string;
    antwort: string;
    status: FrageStatus;
    /** Schon in die Unterlage uebernommen — die Karte erscheint gedimmt. */
    uebernommenAm: string | null;
    rechtsgrundlage: string[];
}

/**
 * Beantwortete Fragen mit `zielobjekt: 'unterlage'` (Kapitel J und N) in
 * Katalogreihenfolge. `gegenstandslos` bleibt draussen — die Antwort dort
 * sagt, warum es nicht zutrifft, und gehoert nicht in die Unterlage.
 */
export const fragenAusPlanung = (fragen: readonly Planungsfrage[]): UfsaFrageVorschlag[] =>
    sortiereFragen([...(fragen || [])])
        .filter(f => f.zielobjekt === 'unterlage' && f.status !== 'gegenstandslos' && (f.antwort || '').trim())
        .map(f => ({
            id: f.id,
            code: f.code,
            frage: f.frage,
            antwort: f.antwort.trim(),
            status: f.status,
            uebernommenAm: f.uebernommen_am || null,
            rechtsgrundlage: [...(f.rechtsgrundlage || [])],
        }));

// ═══════════════════════════════════════════════════════════════════════════
// FRAGE + ANTWORT → VORBELEGUNG EINES EINTRAGS
// ═══════════════════════════════════════════════════════════════════════════

/** Was das Eintragsformular aus einer Karte vorbelegt bekommt. */
export interface UfsaEintragVorbelegung {
    /** Wechselt mit jeder Vorbelegung — das Formular uebernimmt nur neue Schluessel. */
    key: string;
    eintrag: Omit<RabEntry, 'id'>;
    /** Kurzer Herkunftstext fuer den Hinweis am Formular, z. B. „PL-J-03". */
    herkunft: string;
}

interface Bauteilvorgabe {
    component: string;
    category: string;
    activity: string;
    hazards?: string;
}

/**
 * Bauteil, Kategorie und Art der Arbeit je Katalogfrage — die Antwort wird
 * die Massnahme. Fachlich gepflegte Vorgaben, keine Automatik: der Nutzer
 * sieht alles im Formular und aendert, bevor er speichert. Kategorien sind
 * die der Standardreihenfolge (`ufsaOrdering`), damit nichts ans Ende faellt;
 * `autoCategorize` der Ansicht kann sie beim Hinzufuegen noch verfeinern
 * (Aufzug, Tragwerk).
 */
const BAUTEIL_JE_CODE: Record<string, Bauteilvorgabe> = {
    'PL-J-01': { component: 'Wiederkehrende Arbeiten am Bauwerk', category: 'Sonstiges', activity: 'Reinigung / Wartung' },
    'PL-J-02': { component: 'Dachzugang', category: 'Dach', activity: 'Zugang zur Dachfläche', hazards: 'Absturz' },
    'PL-J-03': { component: 'Absturzsicherung Dach', category: 'Dach', activity: 'Arbeiten auf dem Dach', hazards: 'Absturz' },
    'PL-J-04': { component: 'Lichtkuppeln / Lichtbänder', category: 'Dach', activity: 'Reinigung / Wartung', hazards: 'Durchsturz' },
    'PL-J-05': { component: 'Fassade / Verglasung', category: 'Fassade', activity: 'Reinigung', hazards: 'Absturz' },
    'PL-J-06': { component: 'Fassadenbefahranlage', category: 'Fassade', activity: 'Wartung / Prüfung', hazards: 'Absturz' },
    'PL-J-07': { component: 'Technische Anlagen (Zugang)', category: 'Technik', activity: 'Wartung' },
    'PL-J-08': { component: 'Austausch großer Bauteile', category: 'Technik', activity: 'Austausch / Transport' },
    'PL-J-09': { component: 'Leuchten / Melder in großer Höhe', category: 'Innenraum', activity: 'Wartung / Leuchtmittelwechsel', hazards: 'Absturz' },
    'PL-J-10': { component: 'Aufzugsanlage', category: 'Technik', activity: 'Wartung / Rettung' },
    'PL-J-11': { component: 'Bauteile mit Gefahrstoffen', category: 'Sonstiges', activity: 'Umbau / Instandsetzung', hazards: 'Gefahrstoffe' },
    'PL-J-12': { component: 'Verdeckte Leitungen / Trassen', category: 'Innenraum', activity: 'Kernbohrungen / Umbau' },
    'PL-J-13': { component: 'Tragwerk (Belastungsgrenzen)', category: 'Sonstiges', activity: 'Umbau / Durchbrüche' },
    'PL-J-14': { component: 'Absperrarmaturen / Not-Aus', category: 'Technik', activity: 'Instandhaltung' },
    'PL-J-15': { component: 'Übergabe der Unterlage', category: 'Sonstiges', activity: 'Übergabe / Fortschreibung' },
    'PL-J-16': { component: 'Fortschreibung der Unterlage', category: 'Sonstiges', activity: 'Übergabe / Fortschreibung' },
    'PL-N-05': { component: 'Zwischenstand der Unterlage', category: 'Sonstiges', activity: 'Übergabe / Fortschreibung' },
};

/**
 * Eintrag aus Frage und Antwort. Bei bekannter Katalogfrage tragen Bauteil
 * und Art der Arbeit die Frage, die Antwort wird die Massnahme. Bei fremden
 * Codes (eigene Fragen) steht die Frage mit in der Massnahme — nichts geht
 * verloren, das Bauteil traegt der Nutzer nach. Rechtsgrundlagen der Frage
 * werden die Regelwerke des Eintrags.
 */
export const eintragAusFrage = (f: Pick<UfsaFrageVorschlag, 'code' | 'frage' | 'antwort' | 'rechtsgrundlage'>): Omit<RabEntry, 'id'> => {
    const vorgabe = BAUTEIL_JE_CODE[f.code];
    const antwort = (f.antwort || '').trim();
    return {
        component: vorgabe?.component ?? '',
        category: vorgabe?.category ?? '',
        activity: vorgabe?.activity ?? '',
        hazards: vorgabe?.hazards ?? '',
        measures: vorgabe ? antwort : `${(f.frage || '').trim()}\nAntwort: ${antwort}`,
        frequency: '',
        regulations: (f.rechtsgrundlage || []).filter(Boolean).join(', '),
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// PFLICHTSTAND IM KOPF
// ═══════════════════════════════════════════════════════════════════════════

export type UfsaPflichtTon = 'laedt' | 'pflicht' | 'ungeklaert' | 'keine';

export interface UfsaPflichtAnzeige {
    ton: UfsaPflichtTon;
    text: string;
    /** Begruendung bzw. Lesefehler — fuer den Tooltip. */
    detail: string;
}

/**
 * Pflichtstand der Unterlage (§ 3 Abs. 2 Nr. 3 BaustellV) fuer die Kopfzeile.
 * Vor dem Lesen `laedt`. Ein Lesefehler der Planungsphase ist IMMER
 * `ungeklaert` mit dem Fehler als Grund — nie `keine`: aus einem Fehler darf
 * kein Leser eine Entwarnung lesen.
 */
export const pflichtAnzeige = (stand: {
    bereit: boolean;
    steckbrief: ProjektSteckbrief | null;
    fehler: string | null;
}): UfsaPflichtAnzeige => {
    if (!stand.bereit || !stand.steckbrief) {
        return { ton: 'laedt', text: 'Pflichtstand wird gelesen …', detail: '' };
    }
    const sb = stand.steckbrief;
    if (sb.lesefehler.planungsphase) {
        return {
            ton: 'ungeklaert',
            text: 'UfsA-Pflicht ungeklärt',
            detail: `Planungsphase konnte nicht gelesen werden: ${sb.lesefehler.planungsphase}`,
        };
    }
    const p = pflichtAus(sb, 'ufsa');
    if (!p) {
        return { ton: 'ungeklaert', text: 'UfsA-Pflicht ungeklärt', detail: stand.fehler || 'Der Pflichtstand wird nicht geführt.' };
    }
    const detail = [p.begruendung, p.rechtsgrundlage].filter(Boolean).join(' — ');
    if (p.stand === 'pflicht') return { ton: 'pflicht', text: 'UfsA: Pflicht', detail };
    if (p.stand === 'keine') return { ton: 'keine', text: 'UfsA: keine Pflicht', detail };
    return { ton: 'ungeklaert', text: 'UfsA-Pflicht ungeklärt', detail };
};
